// =====================================================
// Chat Window - Main Chat Container
// =====================================================

import React, { useEffect, useRef } from 'react';
import { useChatStore } from '../store/chatStore';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import LoadingIndicator from './LoadingIndicator';
import PhotoEnlargementModal from './PhotoEnlargementModal';
import VirtualTourModal from './VirtualTourModal';
import { sendMessage } from '../services/api';

const ChatWindow: React.FC = () => {
  const {
    isOpen,
    isLoading,
    messages,
    sessionId,
    userId,
    language,
    currentPage,
    addMessage,
    setSessionId,
    setIsLoading
  } = useChatStore();
  const windowRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);

  // Listen for chatbot-action events from card buttons (Floor Plans, Quick Move-Ins, etc.)
  useEffect(() => {
    const handleChatbotAction = async (event: CustomEvent<{ action: string }>) => {
      const { action } = event.detail;
      if (!action) return;

      setIsLoading(true);

      try {
        const response = await sendMessage({
          session_id: sessionId,
          user_id: userId,
          message: action,
          language,
          current_url: currentPage
        });

        // Store session ID from response BEFORE adding message
        // This ensures sessionId is available when buttons render
        if (response.session_id && !sessionId) {
          setSessionId(response.session_id);
        }

        // Add bot response (sessionId is now in store for button clicks)
        addMessage({
          id: `msg_${Date.now()}`,
          sender: 'bot',
          text: response.message,
          timestamp: new Date(),
          buttons: response.buttons || [],
          card: response.card,
          cards: response.cards,
          dropdown: response.dropdown,
          showAppointmentForm: response.showAppointmentForm
        });
      } catch (error) {
        console.error('Error handling chatbot action:', error);
        addMessage({
          id: `msg_${Date.now()}`,
          sender: 'bot',
          text: language === 'en'
            ? 'Sorry, I encountered an error. Please try again.'
            : 'Lo siento, encontré un error. Por favor, inténtalo de nuevo.',
          timestamp: new Date()
        });
      } finally {
        setIsLoading(false);
      }
    };

    window.addEventListener('chatbot-action', handleChatbotAction as EventListener);
    return () => {
      window.removeEventListener('chatbot-action', handleChatbotAction as EventListener);
    };
  }, [sessionId, language, currentPage, addMessage, setSessionId, setIsLoading]);

  useEffect(() => {
    // Initialize chat with greeting when opened for the first time
    const initializeChat = async () => {
      if (isOpen && messages.length === 0 && !hasInitialized.current) {
        hasInitialized.current = true;
        setIsLoading(true);

        try {
          // Send initial "main_menu" action to get the greeting
          const response = await sendMessage({
            session_id: sessionId,
            user_id: userId,
            message: 'main_menu',
            language,
            current_url: currentPage
          });

          // Store session ID from response BEFORE adding message
          // This ensures sessionId is available when buttons render (fixes race condition)
          if (response.session_id && !sessionId) {
            setSessionId(response.session_id);
          }

          // Add bot's greeting message (sessionId is now in store for button clicks)
          addMessage({
            id: Date.now().toString(),
            sender: 'bot',
            text: response.message,
            timestamp: new Date(),
            buttons: response.buttons || [],
            card: response.card,
            cards: response.cards,
            dropdown: response.dropdown,
            showAppointmentForm: response.showAppointmentForm
          });
        } catch (error) {
          console.error('Failed to load initial greeting:', error);

          // Get time-based greeting for fallback
          const hour = new Date().getHours();
          let timeGreeting = 'Hello!';
          if (hour >= 5 && hour < 12) timeGreeting = 'Good morning!';
          else if (hour >= 12 && hour < 17) timeGreeting = 'Good afternoon!';
          else if (hour >= 17 && hour < 21) timeGreeting = 'Good evening!';

          // Fallback greeting if API fails
          addMessage({
            id: Date.now().toString(),
            sender: 'bot',
            text: `${timeGreeting} I am Ava, the digital assistant for Betenbough Homes. How can I assist you?`,
            timestamp: new Date(),
            buttons: [
              { key: 'look_for_home', label: 'Look for a Home', action: 'look_for_home' },
              { key: 'homeowner_resources', label: 'Current Homeowner Resources', action: 'homeowner_resources' }
            ]
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    initializeChat();
  }, [isOpen, messages.length, sessionId, language, currentPage, addMessage, setSessionId, setIsLoading]);

  if (!isOpen) return null;

  return (
    <div
      ref={windowRef}
      className="chat-window fixed z-50 bg-betenbough-alabaster shadow-2xl flex flex-col
                 animate-slide-up overflow-hidden rounded-2xl
                 bottom-4 right-4
                 w-[calc(100vw-32px)] max-w-[420px]
                 h-[calc(100vh-100px)] max-h-[680px]"
    >
      <ChatHeader />

      <div className="flex-1 flex flex-col overflow-hidden bg-betenbough-alabaster">
        <MessageList />
        {isLoading && <LoadingIndicator />}
      </div>

      <ChatInput />

      {/* Photo Enlargement Modal - contained within chat window */}
      <PhotoEnlargementModal />

      {/* Virtual Tour Modal - contained within chat window */}
      <VirtualTourModal />
    </div>
  );
};

export default ChatWindow;